<template>
  <div>
    <!-- Input que muestra el rango seleccionado y abre el modal -->
    <input
      type="text"
      class="form-control numericRange mt-1"
      placeholder="Selecciona un rango"
      readonly
      @click="toggleModal"
      :value="rangeDisplay"
      ref="inputElement"
    />

    <!-- Modal para seleccionar el rango -->
    <div class="modal" ref="rangeModal" tabindex="-1" v-show="showModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="mb-3">
              <label for="desde" class="form-label">Desde:</label>
              <input
                id="desde"
                class="form-control"
                v-maska
                data-maska="9.99#,##"
                data-maska-tokens="9:[0-9]:repeated"
                data-maska-reversed
                inputmode="numeric"
                v-model="desde"
                type="search"
              />
            </div>
            <div class="mb-3">
              <label for="hasta" class="form-label">Hasta:</label>
              <input
                id="hasta"
                class="form-control"
                v-maska
                data-maska="9.99#,##"
                data-maska-tokens="9:[0-9]:repeated"
                data-maska-reversed
                inputmode="numeric"
                v-model="hasta"
                type="search"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Cerrar
            </button>
            <button class="btn btn-white me-5px" @click="resetRange">
              Borrar
            </button>
            <button type="button" class="btn btn-primary" @click="applyRange">
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from "vue";
// mask para el formateo de los importes
import { Mask } from "maska";
// props
const props = defineProps<{
  clear: number;
}>();
// variables
const desde = ref<string | null>(null);
const hasta = ref<string | null>(null);
const fromValue = ref<number | null>(null);
const toValue = ref<number | null>(null);
const showModal = ref(false);
const rangeModal = ref<HTMLDivElement | null>(null);
const inputElement = ref<HTMLInputElement | null>(null);
// emisión de eventos
const emit = defineEmits(["update-range"]);

const vMaska = Mask;
// función para convertir el string de números a números
const parseSpanishNumber = (str: string | null): number | null => {
  if (str === null || str.trim() === "") {
    return null;
  }
  // se reemplaza el separador de miles del string por vacío y la coma decimal por un punto
  const normalizedStr = str.replace(/\./g, "").replace(",", ".");
  // se convierte el string a número
  const parsed = parseFloat(normalizedStr);
  return isNaN(parsed) ? null : parsed;
};

// función para convertir los strings a número y construir el placeholder del input
const rangeDisplay = computed(() => {
  fromValue.value = parseSpanishNumber(desde.value);
  toValue.value = parseSpanishNumber(hasta.value);

  if (fromValue.value !== null && toValue.value !== null) {
    return `Desde: ${desde.value} - Hasta: ${hasta.value}`;
  }
  return ""; // se retorna una cadena vacía si los valores son inválidos
});

// abre / cierra el modal al hacer click en el input
const toggleModal = () => {
  showModal.value = !showModal.value;
  if (showModal.value) {
    nextTick(() => {
      positionModal();
    });
  }
};

// cierra el modal al hacer click en cerrar
const closeModal = () => {
  showModal.value = false;
};

// se comprueba que existen valores y que el rango introducido es válido al hacer click en aplicar y
// Se emite evento con los valores si el rango está ok
const applyRange = () => {
  if (
    fromValue.value !== null &&
    toValue.value !== null &&
    toValue.value >= fromValue.value
  ) {
    emit("update-range", { desde: fromValue.value, hasta: toValue.value });
    closeModal();
  } else {
    alert("Por favor, ingresa un rango válido.");
  }
};

// se borran los datos de los inputs al hacer click en borrar
const resetRange = () => {
  desde.value = "";
  hasta.value = "";
};

// calculo d la posición del modal en función de donde se encuentre el input que lo abre
const positionModal = () => {
  if (inputElement.value && rangeModal.value) {
    const inputRect = inputElement.value.getBoundingClientRect();

    // se pñosiciona el modal justo debajo del input
    rangeModal.value.style.position = "absolute";
    rangeModal.value.style.top = `${inputRect.bottom + window.scrollY - 45}px`;
    rangeModal.value.style.left = `${inputRect.left + window.scrollX - 25}px`;
    rangeModal.value.style.width = `${inputRect.width}px`; // Match the width of the input
    rangeModal.value.style.display = "block"; // Ensure the modal is displayed
  }
};

// Watch para escuchar cuando cambie showModal y resetear los stilos a default al cerrar
watch(showModal, (newVal) => {
  if (!newVal && rangeModal.value) {
    rangeModal.value.style.position = "";
    rangeModal.value.style.top = "";
    rangeModal.value.style.left = "";
    rangeModal.value.style.width = "";
    rangeModal.value.style.display = "";
  }
});

// watch que eswcucha cuando en el padre se hizo click en borrar y limpia el input
watch(
  () => props.clear,
  (newVal) => {
    resetRange();
  },
  { immediate: true }
);
</script>

<style scoped>
/* cursor del input */
.numericRange {
  cursor: pointer;
}
/* por defecto el modal esta oculto */
.modal {
  display: none;
}
/* eliminación del márgen por defecto */
.modal-dialog {
  margin: 0;
}
/* eliminar el centrado por defecto */
.modal-dialog-centered {
  max-width: none; /* eliminación del max-width por defecto */
  width: auto; /* se ajusta el width al contenido */
}
/* padding del body del modal */
.modal-body {
  padding: 1rem;
}
</style>
